import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { HelpSection } from 'legacy/lut/components/widgets/public/partner360/helpButton'
import { WidgetHeaderWithHelpButton } from 'legacy/lut/components/widgets/public/partner360/widgetHeader'
import { Table } from 'legacy/lut/library/list'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { IColumn } from 'core-components/library/list/types'
import { Loader } from 'core-components/library/loader'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { IFilter, Logic, Operator } from 'core-components/types'
import { CheckAccessHelper } from 'core/classes/helpers/CheckAccessHelper'
import { toFormat } from 'core/classes/helpers/DateHelper'
import { ADMIN } from 'core/constants/roles'
import { CrudStore } from 'core/types/crudStore'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'
import { useHoldPlanSectionOpen } from 'partner360/lib/planSectionBusy'
import { useNetworkDebugSource } from 'shared/lib/networkDebug'

import { restoreAcknowledgment } from '../api/restoreAcknowledgment'

import styles from './AcknowledgmentHistory.module.scss'

export type AcknowledgmentHistoryProps = {
  partnerId: string
  fYear: number
  offering: string
  isDistributor: boolean
  flexWidth: FlexDirectionBreak
  onRestored?: () => void | Promise<void>
}

const COLUMNS: IColumn[] = [
  {
    field: 'ProgramType',
    title: 'jbp-commitment-history-program-type',
    priority: 1,
  },
  {
    field: 'ActionDate',
    title: 'jbp-commitment-history-action-date',
    priority: 2,
    renderFunction: (item, column) => (
      <div>{item[column.field] ? toFormat(item[column.field], 'YYYY-MM-DD HH:mm:ss') : ''}</div>
    ),
  },
  {
    field: 'UserEmail',
    title: 'jbp-commitment-history-user-email',
    priority: 3,
  },
  {
    field: 'CommitmentType',
    title: 'jbp-commitment-history-commitment-type',
    priority: 4,
  },
  {
    field: 'ActionName',
    title: 'jbp-commitment-history-action-name',
    priority: 5,
  },
  {
    field: 'Comment',
    title: 'jbp-commitment-history-comment',
    priority: 6,
  },
]

/**
 * The acknowledgment log of one partner's offering, with the Super Admin's way to restore the
 * acknowledgment. A distributor's log spans every offering, as its acknowledgment does.
 */
export const AcknowledgmentHistory = ({
  partnerId,
  fYear,
  offering,
  isDistributor,
  flexWidth,
  onRestored,
}: AcknowledgmentHistoryProps) => {
  const [sourceUpdateCount, setSourceUpdateCount] = useState(0)
  const [isRestoreConfirmationVisible, setIsRestoreConfirmationVisible] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const debugSource = useNetworkDebugSource()

  useHoldPlanSectionOpen(isRestoring)
  const user = useSelector(selectUser)

  const historyCrud = CrudStore.get(CrudStoreItemPartner360.JBPCommitmentLogView)

  const isSuperAdmin = useMemo(() => CheckAccessHelper(user.roleIds, [ADMIN]), [user.roleIds])

  useLayoutEffect(() => {
    if (!partnerId || !fYear || !offering) {
      return
    }

    const filters: (IFilter | null)[] = [
      { field: 'PartnerId', operator: Operator.EQUAL, value: partnerId },
      { field: 'FYear', operator: Operator.EQUAL, value: fYear },
      isDistributor ? null : { field: 'ProgramType', operator: Operator.EQUAL, value: offering },
    ]

    historyCrud.setDefaultFilter({
      logic: Logic.AND,
      filters: filters.filter((it): it is IFilter => it !== null),
    })

    setSourceUpdateCount((value) => value + 1)
  }, [historyCrud, partnerId, fYear, offering, isDistributor])

  const onRestore = useCallback(async () => {
    setIsRestoreConfirmationVisible(false)
    setIsRestoring(true)

    try {
      await restoreAcknowledgment({
        partnerId,
        fYear,
        programType: offering,
        isDistributor,
        debugSource,
      })
      historyCrud.removeQueries(true)
      setSourceUpdateCount((value) => value + 1)
      await onRestored?.()
    } finally {
      setIsRestoring(false)
    }
  }, [debugSource, historyCrud, isDistributor, offering, onRestored, partnerId, fYear])

  return (
    <div>
      <Flex
        className={styles.title}
        justifyContent="space-between"
        alignItems="baseline"
        width="100%"
      >
        <RenderWhen condition={isSuperAdmin}>
          <Button
            className={styles.restoreButton}
            onClick={() => setIsRestoreConfirmationVisible(true)}
            disabled={isRestoring}
          >
            {isRestoring ? (
              <Loader dimension={{ height: 20, width: 20 }} />
            ) : (
              <Translation text="jbp-commitment-restore" />
            )}
          </Button>
        </RenderWhen>

        <Flex flexGrow={1} justifyContent="center">
          <WidgetHeaderWithHelpButton section={HelpSection.HistoryAcknowledgment}>
            jbp-commitment-history-header
          </WidgetHeaderWithHelpButton>
        </Flex>
      </Flex>

      <Table
        flexWidth={flexWidth}
        header
        columns={COLUMNS}
        source={historyCrud}
        sourceUpdateCount={sourceUpdateCount}
        hasTranslate
        pagination
        hasRefresh
      />

      <ConfirmationDialog
        title="jbp-commitment-restore-confirm-title"
        content="jbp-commitment-restore-confirm"
        confirmBtnText="jbp-commitment-restore-confirm-confirm"
        rejectBtnText="jbp-commitment-restore-confirm-reject"
        isVisible={isRestoreConfirmationVisible}
        onConfirm={onRestore}
        onReject={() => setIsRestoreConfirmationVisible(false)}
      />
    </div>
  )
}
